import React, { useState } from "react";
import RefreshToken from "../../../components/refreshToken";
import { useAuth } from "../../../firebase/AuthContext";
import { getApiUrl } from "../../../config/api";

export default function OverviewPanel({
    setActiveTab,
    roles,
    groups,
    audits,
    stats,
    language,
}) {
    const { currentUser } = useAuth();
    const [roleCheckResult, setRoleCheckResult] = useState(null); // null | { roles } | { error }
    const [roleCheckLoading, setRoleCheckLoading] = useState(false);

    const checkMyRoles = async () => {
        setRoleCheckLoading(true);
        setRoleCheckResult(null);
        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${getApiUrl()}/api/v2/user/roles`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (res.ok) {
                setRoleCheckResult({ roles: data.roles || [] });
            } else {
                setRoleCheckResult({ error: data.error || "Unknown error" });
            }
        } catch (err) {
            setRoleCheckResult({ error: err.message });
        } finally {
            setRoleCheckLoading(false);
        }
    };
    return (
        <div className="space-y-6">
            <div className="flex flex-row justify-between">
                <h2 className="text-2xl font-semibold text-gray-900">
                    {language === "en" ? "System Overview" : "系統概覽"}
                </h2>
                <div className="flex items-start gap-2">
                    {/* Check My Roles button */}
                    <div className="flex flex-col items-end gap-2">
                        <button
                            onClick={checkMyRoles}
                            disabled={roleCheckLoading}
                            className="inline-flex items-center gap-2 px-4 py-3 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 rounded-xl text-sm text-white font-medium transition-all duration-300 shadow-lg"
                            title="Check current user roles from backend"
                        >
                            {roleCheckLoading ? (
                                <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            )}
                            {language === "en" ? "Check My Roles" : "檢查我的權限"}
                        </button>

                        {/* Inline result */}
                        {roleCheckResult && (
                            <div className={`text-xs rounded-lg px-3 py-2 max-w-xs text-right ${roleCheckResult.error ? "bg-red-50 border border-red-200 text-red-700" : "bg-indigo-50 border border-indigo-200 text-indigo-800"}`}>
                                {roleCheckResult.error ? (
                                    <span>⚠ {roleCheckResult.error}</span>
                                ) : roleCheckResult.roles.length === 0 ? (
                                    <span className="text-gray-500">{language === "en" ? "No roles assigned" : "尚未指派角色"}</span>
                                ) : (
                                    <div className="flex flex-wrap gap-1 justify-end">
                                        {roleCheckResult.roles.map((r) => (
                                            <span key={r} className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">{r}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <RefreshToken className="md:block hidden" />
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 md:gap-6 gap-0">
                <button
                    className="rounded-lg text-white"
                    onClick={() => setActiveTab("groups")}
                >
                    <div className="flex flex-row items-center justify-between md:py-5 py-2">
                        <div className="bg-dashboard-bg p-4 w-4/5 rounded-l-lg text-left">
                            <p className="text-purple-100 text-sm">
                                {language === "en" ? "User Groups" : "用戶群組"}
                            </p>
                            <p className="text-3xl font-bold">
                                {stats?.groups?.total || groups.length}
                            </p>
                        </div>
                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 w-1/5 p-4 rounded-r-lg flex items-center justify-center">
                            <div className="p-4 bg-purple-400 rounded-full">
                                <svg
                                    className="w-6 h-6"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </button>

                <button
                    className="rounded-lg text-white"
                    onClick={() => setActiveTab("roles")}
                >
                    <div className="flex flex-row items-center justify-between md:py-5 py-2">
                        <div className="bg-dashboard-bg p-4 w-4/5 rounded-l-lg text-left">
                            <p className="text-green-100 text-sm">
                                {language === "en"
                                    ? "Active Roles"
                                    : "活躍角色"}
                            </p>
                            <p className="text-3xl font-bold">{roles.length}</p>
                        </div>
                        <div className="bg-gradient-to-r from-green-500 to-green-600 w-1/5 p-4 rounded-r-lg flex items-center justify-center">
                            <div className="p-4 bg-green-400 rounded-full">
                                <svg
                                    className="w-6 h-6"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>
                </button>

                {/* <button
                    className="rounded-lg text-white"
                    onClick={() => setActiveTab("audits")}
                >
                    <div className="flex items-center justify-between md:py-5 py-2">
                        <div className="bg-dashboard-bg p-4 w-4/5 rounded-l-lg text-left">
                            <p className="text-orange-100 text-sm">
                                {language === "en"
                                    ? "Recent Actions"
                                    : "最近操作"}
                            </p>
                            <p className="text-3xl font-bold">
                                {stats?.recent_actions || audits.length}
                            </p>
                        </div>
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 w-1/5 p-4 rounded-r-lg flex items-center justify-center">
                            <div className="p-4 bg-orange-400 rounded-full">
                                <svg
                                    className="w-6 h-6"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>
                </button> */}
            </div>

            {/* Quick Actions */}
            {/* <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {language === "en" ? "Quick Actions" : "快速操作"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button className="flex items-center p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
                        <div className="p-2 bg-blue-100 rounded-lg mr-3">
                            <svg
                                className="w-5 h-5 text-blue-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                            </svg>
                        </div>
                        <span className="font-medium text-gray-900">
                            {language === "en" ? "Add New User" : "添加新用戶"}
                        </span>
                    </button>

                    <button className="flex items-center p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
                        <div className="p-2 bg-green-100 rounded-lg mr-3">
                            <svg
                                className="w-5 h-5 text-green-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <span className="font-medium text-gray-900">
                            {language === "en" ? "Create Role" : "創建角色"}
                        </span>
                    </button>

                    <button className="flex items-center p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
                        <div className="p-2 bg-purple-100 rounded-lg mr-3">
                            <svg
                                className="w-5 h-5 text-purple-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                            </svg>
                        </div>
                        <span className="font-medium text-gray-900">
                            {language === "en" ? "Manage Groups" : "管理群組"}
                        </span>
                    </button>
                </div>
            </div> */}

            {/* Recent Activity */}
            <div className="bg-white rounded-lg border">
                <div className="px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {language === "en" ? "Recent Activity" : "最近活動"}
                    </h3>
                </div>
                <div className="divide-y">
                    {audits.slice(0, 5).map((audit) => (
                        <div
                            key={audit.id}
                            className="px-6 py-4 flex items-center justify-between"
                        >
                            <div className="flex items-center">
                                <div
                                    className={`w-3 h-3 rounded-full mr-3 ${
                                        audit.status === "success"
                                            ? "bg-green-400"
                                            : "bg-red-400"
                                    }`}
                                />
                                <div>
                                    <p className="text-sm font-medium text-gray-900">
                                        {audit.action}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        {audit.details}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-gray-500">
                                    {audit.actor}
                                </p>
                                <p className="text-xs text-gray-400">
                                    {audit.timestamp}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
